// src/myPlans/PlanPurchasePage.js

import React, { useState, useEffect } from 'react';
import './planPurchasePage.css';
import checkmarkIcon from '../assets/images/icons/checkmark-icon.png';
import { auth, db } from '../firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import PlanConfirmation from './PlanConfirmation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUB_KEY);

const TIERS = [
  { label: '1,000 minutes', value: 1000, monthly: 20, yearly: 16 },
  { label: '2,000 minutes', value: 2000, monthly: 40, yearly: 32 },
];

export default function PlanPurchasePage({ onClose }) {
  // Prevent background scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Firestore + auth
  const user = auth.currentUser;
  const [currentPlan, setCurrentPlan]             = useState(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(null);
  const [billingCycleEnd, setBillingCycleEnd]     = useState(null);
  const [paymentInterval, setPaymentInterval]     = useState(null);
  const [paymentMethod, setPaymentMethod]         = useState(null);
  const [backendPlan, setBackendPlan]             = useState({ creditsLimit: null, price: null });

  // UI
  const [period, setPeriod]                     = useState('yearly');
  const [tierValue, setTierValue]               = useState(TIERS[0].value);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [stripeButtonText, setStripeButtonText] = useState('Get Pro');

  // Derived
  const isPro    = currentPlan?.toLowerCase() === 'pro';
  const tier     = TIERS.find(t => t.value === tierValue) || TIERS[0];
  const displayPrices = { monthly: tier.monthly, yearly: tier.yearly };
  const shownCredits = tierValue;
  const videosCount  = Math.floor(shownCredits / 10);

  // LocalStorage keys
  const planKey     = user && `userCurrentPlan_${user.uid}`;
  const cancelKey   = user && `userCancelAtPeriodEnd_${user.uid}`;
  const billingKey  = user && `userBillingCycleEnd_${user.uid}`;
  const intervalKey = user && `userPaymentInterval_${user.uid}`;
  const methodKey   = user && `userPaymentMethod_${user.uid}`;

  // Hydrate from localStorage or Firestore once
  useEffect(() => {
    if (!user) return;
    const sp = localStorage.getItem(planKey);
    const sc = localStorage.getItem(cancelKey);
    const sb = localStorage.getItem(billingKey);
    const si = localStorage.getItem(intervalKey);
    const sm = localStorage.getItem(methodKey);

    if (sp !== null) setCurrentPlan(sp);
    if (sc !== null) setCancelAtPeriodEnd(sc === 'true');
    if (sb !== null) setBillingCycleEnd(new Date(sb));
    if (si !== null) { setPaymentInterval(si); setPeriod(si); }
    if (sm !== null) setPaymentMethod(sm);

    if ([sp, sc, sb, si, sm].includes(null)) {
      (async () => {
        try {
          const ref  = doc(db, 'users', user.uid, 'subscriptionData', 'planDetails');
          const snap = await getDoc(ref);
          if (!snap.exists()) return;
          const data = snap.data();

          if (data.currentPlan) {
            setCurrentPlan(data.currentPlan);
            localStorage.setItem(planKey, data.currentPlan);
          }
          if (typeof data.cancelAtPeriodEnd === 'boolean') {
            setCancelAtPeriodEnd(data.cancelAtPeriodEnd);
            localStorage.setItem(cancelKey, String(data.cancelAtPeriodEnd));
          }
          if (data.billingCycleEnd?.toDate) {
            const d = data.billingCycleEnd.toDate();
            setBillingCycleEnd(d);
            localStorage.setItem(billingKey, d.toISOString());
          }
          if (data.paymentInterval) {
            setPaymentInterval(data.paymentInterval);
            localStorage.setItem(intervalKey, data.paymentInterval);
            setPeriod(data.paymentInterval);
          }
          if (data.paymentMethod) {
            setPaymentMethod(data.paymentMethod);
            localStorage.setItem(methodKey, data.paymentMethod);
          }
          if (data.currentPlan?.toLowerCase() === 'pro') {
            if (typeof data.creditsLimit === 'number') {
              setBackendPlan(bp => ({ ...bp, creditsLimit: data.creditsLimit }));
              setTierValue(data.creditsLimit);
            }
            if (typeof data.price === 'number') {
              setBackendPlan(bp => ({ ...bp, price: data.price }));
            }
          }
        } catch (err) {
          // console.error('Error fetching planDetails:', err);
        }
      })();
    }
  }, [user]);

  // Real-time Firestore listener
  useEffect(() => {
    if (!user) return;
    const ref = doc(db, 'users', user.uid, 'subscriptionData', 'planDetails');
    const unsub = onSnapshot(ref, snap => {
      if (!snap.exists()) return;
      const data = snap.data();
      if (data.currentPlan) {
        setCurrentPlan(data.currentPlan);
        localStorage.setItem(planKey, data.currentPlan);
      }
      if (typeof data.cancelAtPeriodEnd === 'boolean') {
        setCancelAtPeriodEnd(data.cancelAtPeriodEnd);
        localStorage.setItem(cancelKey, String(data.cancelAtPeriodEnd));
      }
      if (data.billingCycleEnd?.toDate) {
        const d = data.billingCycleEnd.toDate();
        setBillingCycleEnd(d);
        localStorage.setItem(billingKey, d.toISOString());
      }
      if (data.paymentInterval) {
        setPaymentInterval(data.paymentInterval);
        localStorage.setItem(intervalKey, data.paymentInterval);
        setPeriod(data.paymentInterval);
      }
      if (data.paymentMethod) {
        setPaymentMethod(data.paymentMethod);
        localStorage.setItem(methodKey, data.paymentMethod);
      }
      if (data.currentPlan?.toLowerCase() === 'pro') {
        if (typeof data.creditsLimit === 'number') {
          setBackendPlan(bp => ({ ...bp, creditsLimit: data.creditsLimit }));
          setTierValue(data.creditsLimit);
        }
        if (typeof data.price === 'number') {
          setBackendPlan(bp => ({ ...bp, price: data.price }));
        }
      }
    });
    return () => unsub();
  }, [user]);

  // Keep toggle synced
  useEffect(() => {
    if (paymentInterval) setPeriod(paymentInterval);
  }, [paymentInterval]);

  // Free users always see yearly + 1k
  useEffect(() => {
    if (currentPlan?.toLowerCase() === 'free') {
      setPeriod('yearly');
      setTierValue(TIERS[0].value);
    }
  }, [currentPlan]);

  // Format cycle end date
  const endLabel = billingCycleEnd
    ? billingCycleEnd.toLocaleDateString(undefined, {
        year:  'numeric', month: 'long', day: 'numeric'
      })
    : '';

  // Backend values
  const currentPrice   = backendPlan.price;
  const currentCredits = backendPlan.creditsLimit;

  // CTA logic
  let ctaLabel    = '';
  let ctaDisabled = false;

  if (isPro) {
    const sameInterval = period === paymentInterval;
    const sameCredits  = shownCredits === currentCredits;
    if (cancelAtPeriodEnd && sameInterval && sameCredits) {
      ctaLabel    = `Your subscription will cancel on ${endLabel}`;
      ctaDisabled = true;
    } else if (sameInterval && sameCredits) {
      ctaLabel    = 'Your current plan';
      ctaDisabled = true;
    } else {
      ctaLabel    = 'Update Plan';
    }
  } else {
    ctaLabel = 'Get Pro';
  }

  const isCancelledCTA = cancelAtPeriodEnd &&
    period === paymentInterval &&
    shownCredits === currentCredits;

  // Price to confirm
  const confirmPrice = period === 'yearly'
    ? displayPrices.yearly * 12
    : displayPrices.monthly;

  // Stripe button text sync
  useEffect(() => {
    if (ctaLabel === 'Get Pro') setStripeButtonText('Get Pro');
  }, [ctaLabel]);

  // Checkout for new subs
  const startCheckoutSession = async () => {
    setStripeButtonText('Loading…');
    try {
      const functions = getFunctions();
      const processCheckout = httpsCallable(functions, 'processCheckout');
      const gclid = localStorage.getItem('gclid') || '';
      const payload = {
        selectedQuantity: shownCredits / 1000,
        planType: 'pro',
        paymentInterval: period,
        gclid,
      };
      const { data: { id } } = await processCheckout(payload);
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({ sessionId: id });
      if (error) setStripeButtonText('Retry');
    } catch {
      setStripeButtonText('Retry');
    }
  };

  // Update existing sub
  const updateStripeSubscription = async ({ selectedQuantity, planType, paymentInterval }) => {
    try {
      const functions = getFunctions();
      const updateSub = httpsCallable(functions, 'updateStripeSubscription');
      await updateSub({ selectedQuantity, planType, paymentInterval });
      setShowConfirmation(false);
    } catch (err) {
      // TODO: surface error to user
    }
  };

  const selectedQuantity = shownCredits / 1000;
  const planType         = 'pro';

  return (
    <div className="plan-overlay">
      {/* Fixed close button so it never gets clipped */}
      <button
        className="plan-close"
        onClick={onClose}
        aria-label="Close"
      >
        ×
      </button>

      <div className="plan-container">
        <h1 className="plan-title">Upgrade your plan</h1>

        {/* PERIOD TOGGLE */}
        <div className="plan-period-toggle">
          <button className={period==='yearly'?'active':''} onClick={() => setPeriod('yearly')}>
            Annual (Save 20%)
          </button>
          <button className={period==='monthly'?'active':''} onClick={() => setPeriod('monthly')}>
            Monthly
          </button>
        </div>

        {/* TIER SELECT */}
        <div className="plan-tier-select">
          <label htmlFor="tier">Choose usage:</label>
          <div className="credit-usage">
            <select
              id="tier"
              value={tierValue}
              onChange={e => setTierValue(Number(e.target.value))}
            >
              {TIERS.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <FontAwesomeIcon icon={faChevronDown} className="credit-usage__icon" />
          </div>
        </div>

        <div className="plan-card">
          <h2 className="plan-plan-name">Pro</h2>

          {/* PRICE DISPLAY */}
          <div className="plan-price">
            {period==='yearly' ? (
              <>
                <span className="plan-price-old">${displayPrices.monthly}</span>
                <span className="plan-price-amount plan-price-yearly">${displayPrices.yearly}</span>
                <span className="plan-price-unit">USD/mo</span>
              </>
            ) : (
              <>
                <span className="plan-price-dollar">$</span>
                <span className="plan-price-amount">{displayPrices.monthly}</span>
                <span className="plan-price-unit">USD/mo</span>
              </>
            )}
          </div>

          {/* CREDITS */}
          <p className="plan-desc">
            <b>{shownCredits.toLocaleString()} minutes</b>
            <span className="plan-subdesc"> / {videosCount} videos at 10 min each</span>
          </p>

          <button
            className={`plan-cta${ctaDisabled?' disabled':''}${isCancelledCTA?' cancelled':''}`}
            disabled={ctaDisabled}
            onClick={() => {
              if (!ctaDisabled && ctaLabel==='Get Pro') {
                startCheckoutSession();
              } else if (!ctaDisabled && ctaLabel==='Update Plan') {
                setShowConfirmation(true);
              }
            }}
          >
            {ctaLabel==='Get Pro' ? stripeButtonText : ctaLabel}
          </button>

          <ul className="plan-features">
            <li><img src={checkmarkIcon} alt="" /> Everything in Free</li>
            <li><img src={checkmarkIcon} alt="" /> Local video processing</li>
            <li><img src={checkmarkIcon} alt="" /> 3h 30m video length</li>
            <li><img src={checkmarkIcon} alt="" /> Bumpups creator studio</li>
            <li><img src={checkmarkIcon} alt="" /> 16 GB video size</li>
            <li><img src={checkmarkIcon} alt="" /> Aspect ratios (9:16, 1:1, 16:9)</li>
            <li><img src={checkmarkIcon} alt="" /> Social media scheduler</li>
            <li><img src={checkmarkIcon} alt="" /> Custom brand template</li>
          </ul>

          {!isPro && !cancelAtPeriodEnd ? (
            <p className="plan-footnote">
              Need more capabilities for your business?<br/>
              <a href="/enterprise" target="_blank" rel="noopener noreferrer">
                Bumpups Enterprise
              </a>
            </p>
          ) : isPro && !cancelAtPeriodEnd ? (
            <div className="plan-footnote">
              <p>
                <a href="/settings/plan">
                  Manage my subscription
                </a>
              </p>
              <p>
                <a href="https://intercom.help/bumpups/en/collections/7956855-billing" target="_blank" rel="noopener noreferrer">
                  I need help with a billing issue
                </a>
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {showConfirmation && (
        <PlanConfirmation
          price={period==='yearly' ? displayPrices.yearly*12 : displayPrices.monthly}
          paymentMethod={paymentMethod}
          selectedQuantity={selectedQuantity}
          planType={planType}
          paymentInterval={period}
          onClose={() => setShowConfirmation(false)}
          onConfirm={() =>
            updateStripeSubscription({
              selectedQuantity,
              planType,
              paymentInterval: period,
            })
          }
        />
      )}
    </div>
  );
}